import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import api from '../config/axios';
import { decryptWrappedVaultKey } from '../utils/crypto/unwrapVaultKey';
import {
  buildPassphraseWrapper,
  buildRecoveryKeyWrapper,
  buildVaultSetupPayload,
} from '../utils/crypto/vaultSetupCrypto';
import { deriveVaultWrappingKey } from '../utils/crypto/vaultKeyDerivation';
import { importWrappingKeyFromBytes } from '../utils/crypto/importWrappingKey';
import { normalizeVaultKdf } from '../utils/crypto/vaultCryptoUtils';

const VaultContext = createContext(null);

// 10 minutes
const DEFAULT_AUTO_LOCK_MS = 10 * 60 * 1000;

function formatNormalizedRecoveryKey(raw = '') {
  const cleaned = String(raw)
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

  if (!cleaned.startsWith('MSDBRCVR')) {
    throw new Error('Invalid recovery key format');
  }

  const rest = cleaned.slice('MSDBRCVR'.length);
  const chunks = rest.match(/.{1,4}/g) || [];

  return ['MSDB', 'RCVR', ...chunks].join('-');
}

function getWrapperIterations(wrapper) {
  return wrapper?.kdfParams?.iterations ?? wrapper?.iterations ?? undefined;
}

function getWrapperKdf(wrapper) {
  return normalizeVaultKdf(wrapper?.kdfAlgo ?? wrapper?.kdf);
}

export function VaultProvider({ children }) {
  const [sensitiveKey, setSensitiveKey] = useState(null);
  const [isVaultUnlocking, setIsVaultUnlocking] = useState(false);
  const [isVaultSettingUp, setIsVaultSettingUp] = useState(false);
  const [vaultError, setVaultError] = useState('');
  const [vaultStatus, setVaultStatus] = useState(null);
  const [isVaultStatusLoading, setIsVaultStatusLoading] = useState(false);
  const [vaultStatusError, setVaultStatusError] = useState('');

  const autoLockTimerRef = useRef(null);

  const clearAutoLockTimer = useCallback(() => {
    if (autoLockTimerRef.current) {
      clearTimeout(autoLockTimerRef.current);
      autoLockTimerRef.current = null;
    }
  }, []);


  const refreshVaultStatus = useCallback(async () => {
    setIsVaultStatusLoading(true);
    setVaultStatusError('');

    try {
      const { data } = await api.get('/sensitive-vault/status');
      setVaultStatus(data);
      return data;
    } catch (err) {
      const message =
        err?.response?.data?.error || err?.message || 'Failed to load vault status';
      setVaultStatusError(message);
      throw err;
    } finally {
      setIsVaultStatusLoading(false);
    }
  }, []);

  const recordVaultUnlock = useCallback(async () => {
    try {
      const { data } = await api.post('/sensitive-vault/unlock-event');
      setVaultStatus((current) =>
        current
          ? { ...current, lastUnlockedAt: data?.lastUnlockedAt || current.lastUnlockedAt }
          : current
      );
    } catch (err) {
      console.error('Failed to record vault unlock', err);
    }
  }, []);

  const setupVault = useCallback(async ({ passphrase } = {}) => {
    setIsVaultSettingUp(true);
    setVaultError('');

    try {
      const { vaultKey, recoveryKey, payload } = await buildVaultSetupPayload({
        passphrase,
      });

      await api.post('/sensitive-vault/setup', payload);

      setSensitiveKey(vaultKey);
      await recordVaultUnlock();
      await refreshVaultStatus().catch(() => {});

      return {
        recoveryKey,
        vaultKey,
      };
    } catch (err) {
      const message =
        err?.response?.data?.error || err?.message || 'Failed to set up vault';
      setVaultError(message);
      throw err;
    } finally {
      setIsVaultSettingUp(false);
    }
  }, [recordVaultUnlock, refreshVaultStatus]);

  const lockVault = useCallback(() => {
    setSensitiveKey(null);
    setVaultError('');
    clearAutoLockTimer();
  }, [clearAutoLockTimer]);

  const resetAutoLockTimer = useCallback(() => {
    clearAutoLockTimer();

    if (!sensitiveKey) return;

    autoLockTimerRef.current = setTimeout(() => {
      setSensitiveKey(null);
      setVaultError('');
      autoLockTimerRef.current = null;
    }, DEFAULT_AUTO_LOCK_MS);
  }, [clearAutoLockTimer, sensitiveKey]);

  const touchVaultActivity = useCallback(() => {
    if (!sensitiveKey) return;
    resetAutoLockTimer();
  }, [sensitiveKey, resetAutoLockTimer]);

  const vaultExists = useCallback(async () => {
    try {
      await api.get('/sensitive-vault');
      return true;
    } catch (err) {
      if (err?.response?.status === 404) return false;
      throw err;
    }
  }, []);

  const unlockVault = useCallback(async ({ passphrase } = {}) => {
    setIsVaultUnlocking(true);
    setVaultError('');

    try {
      const res = await api.get('/sensitive-vault');
      const { wrappers } = res.data;

      const wrapper = wrappers.find((w) => w.wrapperType === 'passphrase');

      if (!wrapper) {
        throw new Error('Passphrase wrapper not found');
      }

      const kekBytes = await deriveVaultWrappingKey({
        passphrase,
        salt: wrapper.kdfSalt,
        iterations: getWrapperIterations(wrapper),
        kdf: getWrapperKdf(wrapper),
      });

      const kek = await importWrappingKeyFromBytes(kekBytes);

      const vaultKey = await decryptWrappedVaultKey({
        wrappedKey: wrapper.wrappedVaultKey,
        kek,
      });

      setSensitiveKey(vaultKey);
      await recordVaultUnlock();
      return vaultKey;
    } catch (err) {
      const message =
        err?.response?.data?.error || err?.message || 'Failed to unlock vault';
      setVaultError(message);
      throw err;
    } finally {
      setIsVaultUnlocking(false);
    }
  }, [recordVaultUnlock]);

  const unlockVaultWithRecoveryKey = useCallback(
    async ({ recoveryKey } = {}) => {
      setIsVaultUnlocking(true);
      setVaultError('');

      try {
        const normalizedRecoveryKey = formatNormalizedRecoveryKey(recoveryKey);

        const res = await api.get('/sensitive-vault');
        const { wrappers } = res.data;

        const wrapper = wrappers.find((w) => w.wrapperType === 'recovery_key');

        if (!wrapper) {
          throw new Error('Recovery key wrapper not found');
        }

        const kekBytes = await deriveVaultWrappingKey({
          passphrase: normalizedRecoveryKey,
          salt: wrapper.kdfSalt,
          iterations: getWrapperIterations(wrapper),
          kdf: getWrapperKdf(wrapper),
        });

        const kek = await importWrappingKeyFromBytes(kekBytes);

        const vaultKey = await decryptWrappedVaultKey({
          wrappedKey: wrapper.wrappedVaultKey,
          kek,
        });

        setSensitiveKey(vaultKey);
        await recordVaultUnlock();
        return vaultKey;
      } catch (err) {
        const message =
          err?.response?.data?.error ||
          err?.message ||
          'Failed to unlock vault with recovery key';
        setVaultError(message);
        throw err;
      } finally {
        setIsVaultUnlocking(false);
      }
    },
    [recordVaultUnlock]
  );


  const changeVaultPassphrase = useCallback(
    async ({ newPassphrase } = {}) => {
      if (!sensitiveKey) {
        throw new Error('Unlock the Sensitive Vault before changing its passphrase');
      }

      const wrapper = await buildPassphraseWrapper({
        vaultKey: sensitiveKey,
        passphrase: newPassphrase,
        keyVersion: vaultStatus?.keyVersion || 1,
      });

      await api.post('/sensitive-vault/change-passphrase', { wrapper });
      await refreshVaultStatus();
      resetAutoLockTimer();
    },
    [
      refreshVaultStatus,
      resetAutoLockTimer,
      sensitiveKey,
      vaultStatus?.keyVersion,
    ]
  );

  const generateNewVaultRecoveryKey = useCallback(async () => {
    if (!sensitiveKey) {
      throw new Error('Unlock the Sensitive Vault before generating a recovery key');
    }

    const { recoveryKey, wrapper } = await buildRecoveryKeyWrapper({
      vaultKey: sensitiveKey,
      keyVersion: vaultStatus?.keyVersion || 1,
    });

    await api.post('/sensitive-vault/recovery-key', { wrapper });
    await refreshVaultStatus();
    resetAutoLockTimer();
    return recoveryKey;
  }, [
    refreshVaultStatus,
    resetAutoLockTimer,
    sensitiveKey,
    vaultStatus?.keyVersion,
  ]);

  useEffect(() => {
    if (!sensitiveKey) {
      clearAutoLockTimer();
      return;
    }

    resetAutoLockTimer();

    return () => {
      clearAutoLockTimer();
    };
  }, [sensitiveKey, resetAutoLockTimer, clearAutoLockTimer]);

  useEffect(() => {
    return () => {
      clearAutoLockTimer();
    };
  }, [clearAutoLockTimer]);

  const clearVaultError = useCallback(() => {
    setVaultError('');
  }, []);

  const value = useMemo(
    () => ({
      sensitiveKey,
      isVaultUnlocked: Boolean(sensitiveKey),
      isUnlocked: Boolean(sensitiveKey),
      isVaultUnlocking,
      isUnlocking: isVaultUnlocking,
      isVaultSettingUp,
      isSettingUp: isVaultSettingUp,
      vaultError,
      vaultStatus,
      isVaultStatusLoading,
      vaultStatusError,
      unlockVault,
      lockVault,
      clearVaultError,
      vaultExists,
      unlockVaultWithRecoveryKey,
      resetAutoLockTimer,
      touchVaultActivity,
      setupVault,
      refreshVaultStatus,
      changeVaultPassphrase,
      generateNewVaultRecoveryKey,
    }),
    [
      sensitiveKey,
      isVaultUnlocking,
      isVaultSettingUp,
      vaultError,
      vaultStatus,
      isVaultStatusLoading,
      vaultStatusError,
      unlockVault,
      lockVault,
      clearVaultError,
      vaultExists,
      unlockVaultWithRecoveryKey,
      resetAutoLockTimer,
      touchVaultActivity,
      setupVault,
      refreshVaultStatus,
      changeVaultPassphrase,
      generateNewVaultRecoveryKey,
    ]
  );

  return (
    <VaultContext.Provider value={value}>{children}</VaultContext.Provider>
  );
}

export function useVault() {
  const ctx = useContext(VaultContext);

  if (!ctx) {
    throw new Error('useVault must be used inside a VaultProvider');
  }

  return ctx;
}
